import { useState, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ptTime = (p) => (p.last_contact ? new Date(p.last_contact * 1000) : new Date(p.recorded_at));
const dayKey = (p) => ptTime(p).toLocaleDateString("en-AU");
const fmt = (p) => ptTime(p).toLocaleString("en-AU");

const planeIcon = (heading = 0) => L.divIcon({
  className: "",
  html: `<div style="transform:rotate(${heading}deg)"><svg width="30" height="30" viewBox="0 0 24 24" fill="#0F172A" stroke="#84CC16" stroke-width="1.2" style="filter:drop-shadow(0 2px 3px rgba(0,0,0,.4))"><path d="M12 2 L14 11 L22 12 L14 13 L12 22 L10 13 L2 12 L10 11 Z"/></svg></div>`,
  iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -14],
});

function FitBounds({ points }) {
  const map = useMap();
  useMemo(() => {
    if (points.length === 1) map.setView(points[0], 10);
    else if (points.length > 1) map.fitBounds(points, { padding: [30, 30] });
  }, [points, map]);
  return null;
}

export default function AssetTrackMap({ tracks }) {
  const days = useMemo(() => [...new Set(tracks.map(dayKey))], [tracks]);
  const [day, setDay] = useState("all");

  const points = useMemo(() => {
    const filtered = day === "all" ? tracks : tracks.filter((t) => dayKey(t) === day);
    return filtered
      .filter((t) => t.lat != null && t.lon != null)
      .slice()
      .sort((a, b) => (a.last_contact || 0) - (b.last_contact || 0));
  }, [tracks, day]);

  if (points.length === 0) return null;
  const latlngs = points.map((p) => [p.lat, p.lon]);
  const last = points[points.length - 1];

  return (
    <div className="mb-4" data-testid="asset-track-map-wrap">
      {days.length > 1 && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Replay day</span>
          <Select value={day} onValueChange={setDay}>
            <SelectTrigger className="h-8 w-44 text-sm" data-testid="track-day-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All days</SelectItem>
              {days.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height: "300px" }} data-testid="asset-track-map">
        <MapContainer center={latlngs[0]} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={latlngs} />
          <Polyline positions={latlngs} pathOptions={{ color: "#84CC16", weight: 3, opacity: 0.85 }} />
          {points.slice(0, -1).map((p) => (
            <CircleMarker key={p.id} center={[p.lat, p.lon]} radius={4}
              pathOptions={{ color: "#0F172A", fillColor: "#fff", fillOpacity: 1, weight: 2 }}>
              <Tooltip>{fmt(p)}</Tooltip>
              <Popup>{fmt(p)}<br />{p.altitude != null ? `${Math.round(p.altitude)} m` : ""}{p.velocity != null ? ` · ${Math.round(p.velocity * 1.94384)} kt` : ""}</Popup>
            </CircleMarker>
          ))}
          <Marker position={[last.lat, last.lon]} icon={planeIcon(last.heading || 0)}>
            <Tooltip>Latest · {fmt(last)}</Tooltip>
            <Popup>Latest position<br />{fmt(last)}<br />{last.altitude != null ? `${Math.round(last.altitude)} m` : ""}{last.velocity != null ? ` · ${Math.round(last.velocity * 1.94384)} kt` : ""}</Popup>
          </Marker>
        </MapContainer>
      </div>
    </div>
  );
}
